import { type PropsWithChildren } from "react";
import { ButtonBase, type ButtonBaseProps } from "@mui/material";
import { PencilSimpleIcon, PlusIcon } from "@phosphor-icons/react";
import { useTranslations } from "use-intl";
import clsx from "clsx";

import { type Traveller } from "@/app/lib/types/types";
import { IconTraveller, IconTravellerSelected } from "@/app/icons";

type Props = PropsWithChildren<
  Pick<ButtonBaseProps, "onClick"> & {
    traveller: Traveller | null;
  }
>;

export function TravellerSelectionButton({
  children,
  onClick,
  traveller,
}: Props) {
  const t = useTranslations();

  const LeadingIcon = traveller ? IconTravellerSelected : IconTraveller;
  const TrailingIcon = traveller ? PencilSimpleIcon : PlusIcon;

  return (
    <ButtonBase
      sx={{
        backgroundColor: "var(--color-white)",
        borderRadius: "var(--radius-3xl)",
        padding: "calc(var(--spacing) * 4)",
        width: "100%",
        display: "flex",
        gap: "calc(var(--spacing) * 2)",
        alignItems: "center",
        justifyContent: "start",
        textAlign: "start",
      }}
      onClick={onClick}
    >
      <LeadingIcon
        className={clsx(
          "size-10 shrink-0 rounded-full p-2",
          traveller ? "bg-surface-accent-softer" : "bg-surface-background",
        )}
        aria-hidden
      />
      <span className="flex flex-col" data-sentry-mask>
        <span className="text-sm font-medium text-text-primary">
          {traveller
            ? [traveller.firstName, traveller.lastName].join(" ")
            : children}
        </span>
        <span className="text-sm font-normal text-text-tertiary">
          {/* TODO: additional details of a traveller / age for children */}
          {traveller ? children : t("travellers.tapToAdd")}
        </span>
      </span>
      <TrailingIcon
        className="ms-auto shrink-0 text-text-tertiary"
        size={16}
        weight="bold"
        aria-hidden
      />
    </ButtonBase>
  );
}
